import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, FileText, Code, Copy, Check, X, Eye, EyeOff, Users, Pencil, ExternalLink } from 'lucide-react';

const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
const selectContentCls = "bg-[#0A1F1A] border-white/10";

const FIELD_TYPES = [
  { value: 'text', label: 'Texto', placeholder: 'Ej: Nombre completo' },
  { value: 'email', label: 'Email', placeholder: 'Ej: correo@email.com' },
  { value: 'tel', label: 'Telefono', placeholder: 'Ej: 300 123 4567' },
  { value: 'number', label: 'Numerico', placeholder: 'Ej: 500000000' },
  { value: 'select', label: 'Lista de opciones', placeholder: 'Opciones separadas por coma' },
  { value: 'textarea', label: 'Texto largo', placeholder: 'Ej: Comentarios' },
];

const CRM_FIELD_MAP = [
  { value: '', label: 'Sin mapear (extra)' },
  { value: 'name', label: 'Nombre (CRM)' },
  { value: 'phone', label: 'Telefono (CRM)' },
  { value: 'email', label: 'Email (CRM)' },
  { value: 'budget', label: 'Presupuesto (CRM)' },
  { value: 'sector', label: 'Sector (CRM)' },
  { value: 'rooms', label: 'Habitaciones (CRM)' },
  { value: 'property_type', label: 'Tipo de propiedad (CRM)' },
  { value: 'notes', label: 'Notas (CRM)' },
  { value: 'other_characteristics', label: 'Otras caracteristicas (CRM)' },
];

function generateEmbedCode(form, apiUrl, tyGlobal) {
  const fields = form.fields || [];
  const formSlug = form.id.slice(0, 8);
  const tyPotencial = (tyGlobal && tyGlobal.thank_you_url_potencial) || '';
  const tyContacto = (tyGlobal && tyGlobal.thank_you_url_contacto) || '';
  const esc = (v) => String(v || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const label = (t, req = true) => `<label class="sel-label">${t}${req ? ' *' : ''}</label>`;

  // ------- Datos del inmueble (solo lectura) -------
  const propCells = [];
  if (form.property_price) propCells.push(`<div><div class="sel-label">Precio</div><div class="sel-prop-val">${esc(form.property_price)}</div></div>`);
  if (form.property_size) propCells.push(`<div><div class="sel-label">Área</div><div class="sel-prop-val">${esc(form.property_size)}</div></div>`);
  if (form.property_location) propCells.push(`<div><div class="sel-label">Ubicación</div><div class="sel-prop-val">${esc(form.property_location)}</div></div>`);
  const propertyContextHtml = propCells.length ? `<div class="sel-prop">${propCells.join('')}</div>` : '';

  // ------- Preguntas por defecto -------
  const budgetOpts = Array.isArray(form.budget_options) && form.budget_options.length
    ? form.budget_options
    : ['Menos de … millones', 'Entre … y … millones', 'Más de … millones'];
  const timingOpts = ['Inmediato', 'De 1 a 3 meses', 'De 3 a 6 meses', 'Más de 6 meses'];

  const selectHtml = (name, lbl, opts) => `<div class="sel-field">${label(lbl)}
      <select name="${name}" required class="sel-input sel-select">
        <option value="">Seleccionar...</option>
        ${opts.map(o => `<option value="${esc(o)}">${esc(o)}</option>`).join('')}
      </select></div>`;

  const radioHtml = (name, lbl, opts) => `<div class="sel-field">${label(lbl)}
      <div class="sel-radios">
        ${opts.map((o, i) => `<label class="sel-radio"><input type="radio" name="${name}" value="${esc(o)}" ${i === 0 ? 'required' : ''}/><span class="sel-radio-dot"></span><span>${esc(o)}</span></label>`).join('')}
      </div></div>`;

  const defaultFieldsHtml = `
    <div class="sel-grid2">
      <div class="sel-field">${label('Nombre completo')}<input type="text" name="name" placeholder="Tu nombre" required class="sel-input"/></div>
      <div class="sel-field">${label('Teléfono o WhatsApp')}<input type="tel" inputmode="tel" name="phone" placeholder="Número de contacto" required class="sel-input"/></div>
    </div>
    ${selectHtml('decision_timing', '¿Cuándo debes tomar la decisión?', timingOpts)}
    ${radioHtml('budget_bucket', 'Presupuesto máximo', budgetOpts)}`;

  // ------- Campos extra agregados por el admin -------
  const fieldInputs = fields.map(f => {
    const lbl = label(f.label, f.required);
    const crm = `data-crm="${f.crm_map || ''}"`;
    if (f.type === 'select') {
      const opts = (f.options || '').split(',').map(o => o.trim()).filter(Boolean);
      return `<div class="sel-field">${lbl}<select name="${f.key}" ${crm} ${f.required ? 'required' : ''} class="sel-input sel-select"><option value="">Seleccionar...</option>${opts.map(o => `<option value="${esc(o)}">${esc(o)}</option>`).join('')}</select></div>`;
    }
    if (f.type === 'textarea') {
      return `<div class="sel-field">${lbl}<textarea name="${f.key}" ${crm} placeholder="${esc(f.placeholder)}" ${f.required ? 'required' : ''} class="sel-input" style="min-height:110px;resize:vertical"></textarea></div>`;
    }
    return `<div class="sel-field">${lbl}<input type="${f.type}" name="${f.key}" ${crm} placeholder="${esc(f.placeholder)}" ${f.required ? 'required' : ''} class="sel-input"/></div>`;
  }).join('\n  ');

  const mappings = {};
  fields.forEach(f => { if (f.crm_map) mappings[f.key] = f.crm_map; });
  const mappingJson = JSON.stringify(mappings);
  const propSummary = [
    form.property_price ? `Precio: ${form.property_price}` : '',
    form.property_size ? `Área: ${form.property_size}` : '',
    form.property_location ? `Ubicación: ${form.property_location}` : '',
  ].filter(Boolean).join(' · ');

  return `<!-- SELECTO CRM Form: ${form.name} -->
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  .sel-form-${formSlug} { font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif; background:#fff; color:#1a1a1a; max-width:640px; margin:0 auto; padding:8px 0 }
  .sel-form-${formSlug} *, .sel-form-${formSlug} *::before, .sel-form-${formSlug} *::after { box-sizing:border-box }
  .sel-form-${formSlug} .sel-label { display:block; font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:#6b6b6b; margin-bottom:10px }
  .sel-form-${formSlug} .sel-prop { display:grid; grid-template-columns:1fr 1fr; gap:22px 32px; padding:24px 32px; border:1px solid #d9d9d9; border-radius:6px; margin-bottom:34px }
  .sel-form-${formSlug} .sel-prop .sel-label { margin-bottom:6px }
  .sel-form-${formSlug} .sel-prop-val { font-size:19px; font-weight:400; color:#1a1a1a; letter-spacing:-.01em }
  .sel-form-${formSlug} .sel-grid2 { display:grid; grid-template-columns:1fr 1fr; gap:0 32px }
  .sel-form-${formSlug} .sel-field { margin-bottom:28px }
  .sel-form-${formSlug} .sel-input { width:100%; padding:26px 28px; border:1px solid #d9d9d9; background:#fff; color:#1a1a1a; font-size:19px; font-family:inherit; border-radius:6px; outline:none; transition:border-color .2s,box-shadow .2s; -webkit-appearance:none; appearance:none }
  .sel-form-${formSlug} .sel-input:focus { border-color:#1a1a1a; box-shadow:0 0 0 3px rgba(26,26,26,.06) }
  .sel-form-${formSlug} .sel-input::placeholder { color:#a3a3a3; font-weight:400 }
  .sel-form-${formSlug} .sel-select { background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2214%22 height=%229%22 viewBox=%220 0 14 9%22><path d=%22M1 1l6 6 6-6%22 stroke=%22%231a1a1a%22 fill=%22none%22 stroke-width=%221.5%22/></svg>'); background-repeat:no-repeat; background-position:right 26px center; padding-right:60px; cursor:pointer }
  .sel-form-${formSlug} .sel-radios { border-top:1px solid #e3e3e3 }
  .sel-form-${formSlug} .sel-radio { display:flex; align-items:center; gap:22px; padding:24px 6px; border-bottom:1px solid #e3e3e3; font-size:19px; color:#1a1a1a; cursor:pointer; user-select:none }
  .sel-form-${formSlug} .sel-radio input { position:absolute; opacity:0; width:0; height:0 }
  .sel-form-${formSlug} .sel-radio-dot { width:24px; height:24px; border:1.5px solid #b3b3b3; border-radius:50%; flex-shrink:0; position:relative; transition:border-color .15s }
  .sel-form-${formSlug} .sel-radio:hover .sel-radio-dot { border-color:#1a1a1a }
  .sel-form-${formSlug} .sel-radio input:checked + .sel-radio-dot { border-color:#1a1a1a }
  .sel-form-${formSlug} .sel-radio input:checked + .sel-radio-dot::after { content:''; position:absolute; inset:5px; border-radius:50%; background:#1a1a1a }
  .sel-form-${formSlug} .sel-footer { display:flex; align-items:center; justify-content:space-between; gap:24px; padding-top:30px; border-top:1px solid #e3e3e3; flex-wrap:wrap }
  .sel-form-${formSlug} .sel-consent { display:flex; align-items:flex-start; gap:14px; flex:1; min-width:240px }
  .sel-form-${formSlug} .sel-consent input[type=checkbox] { margin-top:3px; width:20px; height:20px; accent-color:#1a1a1a; cursor:pointer; flex-shrink:0 }
  .sel-form-${formSlug} .sel-consent label { font-size:14px; line-height:1.55; color:#5a5a5a; cursor:pointer }
  .sel-form-${formSlug} .sel-consent a { color:#1a1a1a; text-decoration:underline }
  .sel-form-${formSlug} .sel-submit { padding:26px 56px; background:#0a0a0a; color:#fff; border:none; font-size:16px; font-weight:500; letter-spacing:.02em; cursor:pointer; border-radius:999px; font-family:inherit; transition:background .2s,transform .1s; display:inline-flex; align-items:center; gap:12px }
  .sel-form-${formSlug} .sel-submit:hover { background:#2a2a2a }
  .sel-form-${formSlug} .sel-submit:active { transform:scale(.99) }
  .sel-form-${formSlug} .sel-submit svg { width:18px; height:18px; stroke:currentColor; fill:none; stroke-width:2 }
  .sel-form-${formSlug} .sel-msg { text-align:center; margin-top:18px; font-size:14px; color:#2f855a; display:none }
  @media (max-width:640px) { .sel-form-${formSlug} .sel-grid2, .sel-form-${formSlug} .sel-prop { grid-template-columns:1fr } .sel-form-${formSlug} .sel-prop { padding:20px } .sel-form-${formSlug} .sel-input { padding:20px 20px; font-size:17px } .sel-form-${formSlug} .sel-radio { font-size:17px } .sel-form-${formSlug} .sel-submit { width:100%; justify-content:center } }
</style>
<div id="selecto-form-${formSlug}" class="sel-form-${formSlug}">
  ${propertyContextHtml}
  <form onsubmit="return window.handleSelectoForm_${formSlug}(event,'${form.id}','${esc(form.name)}')">
  ${defaultFieldsHtml}
  ${fieldInputs}
    <div class="sel-footer">
      <div class="sel-consent">
        <input type="checkbox" id="sel-consent-${formSlug}" name="consent" required />
        <label for="sel-consent-${formSlug}">Acepto la <a href="https://www.somosselecto.com/politica-de-tratamiento-de-datos-personales" target="_blank" rel="noopener">política de tratamiento de datos personales</a>.</label>
      </div>
      <button type="submit" class="sel-submit"><span>Enviar</span><svg viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
    </div>
    <p id="selecto-msg-${formSlug}" class="sel-msg">Enviado correctamente</p>
  </form>
</div>
<script>
(function(){
  window.handleSelectoForm_${formSlug} = async function(e,formId,formName){
    e.preventDefault();
    var f=e.target,d=new FormData(f),map=${mappingJson},b={form_id:formId,source:formName,extra_fields:{},notes_parts:[]};
    d.forEach(function(v,k){
      if(k==='consent'){b.extra_fields.consent=true;return;}
      if(k==='name'){b.name=v;return;}
      if(k==='phone'){b.phone=v;return;}
      if(k==='budget_bucket'){b.extra_fields.presupuesto=v;return;}
      if(k==='decision_timing'){b.extra_fields.cuando_decision=v;return;}
      var crm=map[k]||'';
      if(crm==='name')b.name=v;else if(crm==='phone')b.phone=v;else if(crm==='email')b.email=v;else if(crm==='budget')b.budget=parseFloat(String(v).replace(/[^0-9.]/g,''))||0;else if(crm==='sector')b.sector=v;else if(crm==='rooms')b.rooms=parseInt(v)||0;else if(crm==='property_type')b.property_type=v;else if(crm==='notes')b.notes_parts.push(v);else if(crm==='other_characteristics')b.other_characteristics=v;else b.extra_fields[k]=v;
    });
    var chosenBudget=(b.extra_fields.presupuesto||'').toString();
    var norm=function(s){return (s||'').toString().normalize('NFD').replace(/[\\u0300-\\u036f]/g,'').toLowerCase();};
    // Regla: "Menos de …" → contacto; "Entre … y …" / "Más de …" → cliente potencial
    var isLowBudget=norm(chosenBudget).indexOf('menos')===0;
    // Rosales: además el tiempo de decisión debe ser Inmediato o De 1 a 3 meses
    if(${/rosales/i.test(form.name.normalize('NFD')) ? 'true' : 'false'}){var tm=norm(b.extra_fields.cuando_decision);if(!(tm.indexOf('inmediat')===0||tm.indexOf('1 a 3')>-1))isLowBudget=true;}
    b.initial_tag_names=[ isLowBudget ? 'contacto' : 'cliente potencial' ];
    // Presupuesto CRM (numérico): toma el mayor valor de la opción; "millones" multiplica x1e6
    var nums=(chosenBudget.match(/\\d[\\d.,]*/g)||[]).map(function(n){return parseFloat(n.replace(/[.,]/g,''))||0;});
    if(nums.length){var mx=Math.max.apply(null,nums);if(/millon/i.test(norm(chosenBudget))&&mx<1000000)mx=mx*1000000;b.budget=mx;}
    // Notas CRM: decisión + presupuesto + datos del inmueble
    if(b.extra_fields.cuando_decision)b.notes_parts.unshift('Decisión: '+b.extra_fields.cuando_decision);
    if(chosenBudget)b.notes_parts.push('Presupuesto máximo: '+chosenBudget);
    if('${esc(propSummary)}')b.notes_parts.push('Inmueble: ${esc(propSummary)}');
    if(b.notes_parts.length)b.notes=b.notes_parts.join(' | ');
    delete b.notes_parts;
    var evId = 'sel-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    var TRAFFIC = '${form.traffic_type || 'auto'}';
    try {
      b.traffic_type = TRAFFIC;
      b.event_source_url = window.location.href;
      b.client_user_agent = navigator.userAgent;
      b.meta_event_id = evId;
      if (TRAFFIC !== 'organico') {
        var qp = new URLSearchParams(window.location.search);
        var store = {};
        if (TRAFFIC === 'pauta') {
          try { store = JSON.parse(localStorage.getItem('selecto_attr') || '{}'); } catch(_e) {}
          if (!store.fbclid) {
            var ck = document.cookie.split(';').reduce(function(a,c){var p=c.trim().split('=');if(p[0])a[p[0]]=p.slice(1).join('=');return a;},{});
            if (ck.selecto_attr) { try { store = JSON.parse(decodeURIComponent(ck.selecto_attr)); } catch(_e) {} }
          }
        }
        ['fbclid','utm_source','utm_medium','utm_campaign','utm_content','utm_term','ad_id','adset_id','campaign_id'].forEach(function(k){
          var v = qp.get(k) || store[k];
          if (v) b[k] = v;
        });
        b.fbclid_source = qp.get('fbclid') ? 'url' : (b.fbclid ? 'stored' : '');
        var cookies = document.cookie.split(';').reduce(function(acc,c){var p=c.trim().split('=');if(p[0])acc[p[0]]=p.slice(1).join('=');return acc;},{});
        if (cookies._fbp) b.fbp = cookies._fbp;
        if (cookies._fbc) b.fbc = cookies._fbc;
      }
    } catch(_){}
    if(!b.name){alert('El nombre es obligatorio');return false;}
    if(!chosenBudget){alert('Selecciona tu presupuesto máximo');return false;}
    try{var r=await fetch('${apiUrl}/api/leads/external',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(b)});
    if(r.ok){
      try{var rj=await r.json();if(rj&&rj.lead_tag_type)isLowBudget=rj.lead_tag_type==='contacto';}catch(_e){}
      f.reset();
      var ty_potencial='${tyPotencial}';
      var ty_contacto='${tyContacto}';
      var ty=isLowBudget ? (ty_contacto||ty_potencial) : (ty_potencial||ty_contacto);
      // El formulario NO dispara ningún evento de Meta. Solo etiqueta y redirige.
      if(ty){window.location.href=ty;}
      else{var m=document.getElementById('selecto-msg-${formSlug}');m.style.display='block';setTimeout(function(){m.style.display='none';},3500);}
    }
    else{alert('Error al enviar');}}catch(err){alert('Error de conexion');}return false;
  };
})();
</script>`;
}

export default function CRMForms() {
  const [forms, setForms] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [showEmbed, setShowEmbed] = useState(null);
  const [embedTab, setEmbedTab] = useState('preview');
  const [copied, setCopied] = useState(false);
  const defaultFields = [];  // The embed now emits the 5 mandatory defaults automatically. Any admin-added fields here are EXTRA fields shown below the defaults.
  const [newForm, setNewForm] = useState({ name: '', fields: [...defaultFields], thank_you_url: '', low_budget_thank_you_url: '', min_budget: '', default_broker_id: '', traffic_type: 'auto',
    property_price: '', property_location: '', property_size: '',
    budget_options: ['Menos de … millones', 'Entre … y … millones', 'Más de … millones']
  });

  const [tyPages, setTyPages] = useState({ thank_you_url_potencial: '', thank_you_url_contacto: '' });
  const [savingTy, setSavingTy] = useState(false);
  const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    api.get('/crm/forms').then(setForms).catch(() => {});
    api.get('/users/brokers').then(setBrokers).catch(() => {});
    api.get('/settings/thank-you-pages').then(setTyPages).catch(() => {});
  }, []);

  const saveTyPages = async () => {
    setSavingTy(true);
    try {
      const r = await api.put('/settings/thank-you-pages', tyPages);
      setTyPages(r);
      toast.success('Thank You Pages actualizadas — aplican a todos los formularios');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
    setSavingTy(false);
  };

  const addField = () => {
    setNewForm(prev => ({ ...prev, fields: [...prev.fields, { key: '', label: '', type: 'text', required: false, options: '' }] }));
  };

  const updateField = (idx, updates) => {
    setNewForm(prev => {
      const fields = [...prev.fields];
      fields[idx] = { ...fields[idx], ...updates };
      if (updates.label && !fields[idx].key) fields[idx].key = updates.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
      return { ...prev, fields };
    });
  };

  const removeField = (idx) => {
    setNewForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));
  };

  const createForm = async () => {
    if (!newForm.name.trim()) return toast.error('Nombre del formulario requerido');
    const cleanBudgetOpts = (newForm.budget_options || []).map(o => (o || '').trim()).filter(Boolean);
    if (cleanBudgetOpts.length < 2) return toast.error('Agrega al menos 2 opciones de presupuesto');
    const norm = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (!cleanBudgetOpts.some(o => norm(o).startsWith('menos que') || norm(o).startsWith('menos de'))) {
      return toast.error('Una opción de presupuesto debe empezar con "Menos que…" (marca la ruta contacto)');
    }
    const payload = {
      name: newForm.name, fields: newForm.fields,
      thank_you_url: newForm.thank_you_url, low_budget_thank_you_url: newForm.low_budget_thank_you_url,
      min_budget: Number(newForm.min_budget) || 0, default_broker_id: newForm.default_broker_id || '',
      traffic_type: newForm.traffic_type || 'auto',
      property_price: newForm.property_price || '',
      property_location: newForm.property_location || '',
      property_size: newForm.property_size || '',
      budget_options: cleanBudgetOpts,
    };
    if (editingForm) {
      const res = await api.put(`/crm/forms/${editingForm.id}`, payload);
      setForms(prev => prev.map(f => f.id === editingForm.id ? res : f));
      toast.success('Formulario actualizado');
    } else {
      const res = await api.post('/crm/forms', payload);
      setForms(prev => [...prev, res]);
      toast.success('Formulario creado');
    }
    closeModal();
  };

  const startEdit = (form) => {
    setEditingForm(form);
    setNewForm({
      name: form.name, fields: [...(form.fields || [])],
      thank_you_url: form.thank_you_url || '', low_budget_thank_you_url: form.low_budget_thank_you_url || '',
      min_budget: form.min_budget || '', default_broker_id: form.default_broker_id || '',
      traffic_type: form.traffic_type || 'auto',
      property_price: form.property_price || '',
      property_location: form.property_location || '',
      property_size: form.property_size || '',
      budget_options: form.budget_options && form.budget_options.length ? form.budget_options : ['Menos de … millones', 'Entre … y … millones', 'Más de … millones'],
    });
    setShowCreate(true);
  };

  const closeModal = () => {
    setShowCreate(false);
    setEditingForm(null);
    setNewForm({ name: '', fields: [...defaultFields], thank_you_url: '', low_budget_thank_you_url: '', min_budget: '', default_broker_id: '', traffic_type: 'auto',
      property_price: '', property_location: '', property_size: '',
      budget_options: ['Menos de … millones', 'Entre … y … millones', 'Más de … millones'],
    });
  };

  const deleteForm = async (id) => {
    if (!window.confirm('Eliminar este formulario?')) return;
    await api.del(`/crm/forms/${id}`);
    setForms(prev => prev.filter(f => f.id !== id));
    toast.success('Formulario eliminado');
  };

  const toggleActive = async (form) => {
    const updated = await api.put(`/crm/forms/${form.id}`, { active: !form.active });
    setForms(prev => prev.map(f => f.id === form.id ? updated : f));
  };

  const copyEmbed = (form) => {
    const code = generateEmbedCode(form, apiUrl, tyPages);
    navigator.clipboard.writeText(code);
    setCopied(form.id);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Codigo copiado al portapapeles');
  };

  return (
    <div data-testid="crm-forms-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white flex items-center gap-3">
            <FileText size={24} strokeWidth={1.5} className="text-[#A38560]" /> Formularios
          </h1>
          <p className="text-sm text-white/50 mt-1">Crea formularios embebibles para capturar leads desde tus landing pages</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-form-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-4 py-2 rounded-xl text-sm transition-all">
          <Plus size={16} /> Nuevo Formulario
        </button>
      </div>

      {/* Global Thank You Pages (single source of truth for ALL forms) */}
      <div data-testid="global-ty-pages" className="bg-[#0A1F1A] rounded-2xl border border-[#A38560]/20 p-4 lg:p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#A38560]/10 border border-[#A38560]/25 flex items-center justify-center shrink-0">
            <ExternalLink size={14} className="text-[#A38560]" />
          </div>
          <div>
            <h2 className="font-outfit text-base font-semibold text-white">Thank You Pages globales</h2>
            <p className="text-[11px] text-white/50">Se aplican a <b>todos</b> los formularios. La ruta depende de la etiqueta que reciba el lead.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-black border border-white/40" /> Cliente potencial
            </Label>
            <Input value={tyPages.thank_you_url_potencial || ''}
              onChange={e => setTyPages({ ...tyPages, thank_you_url_potencial: e.target.value })}
              placeholder="https://somosselecto.com/gracias-cliente-potencial"
              className={inputCls} data-testid="ty-potencial-input" />
          </div>
          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400" /> Contacto (Menos que…)
            </Label>
            <Input value={tyPages.thank_you_url_contacto || ''}
              onChange={e => setTyPages({ ...tyPages, thank_you_url_contacto: e.target.value })}
              placeholder="https://somosselecto.com/gracias-contacto"
              className={inputCls} data-testid="ty-contacto-input" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={saveTyPages} disabled={savingTy} data-testid="save-ty-btn"
            className="px-4 py-2 rounded-xl bg-[#A38560]/20 text-[#A38560] hover:bg-[#A38560]/30 border border-[#A38560]/30 text-sm font-medium transition-colors disabled:opacity-50">
            {savingTy ? 'Guardando…' : 'Guardar Thank You Pages'}
          </button>
        </div>
      </div>

      {/* Forms List */}
      <div className="space-y-4">
        {forms.map(form => (
          <div key={form.id} data-testid={`form-${form.id}`} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-outfit text-lg font-medium text-white truncate">{form.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${form.active ? 'bg-[#6BCB77]/20 text-[#6BCB77]' : 'bg-white/10 text-white/40'}`}>
                    {form.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <p className="text-xs text-white/40 mt-1">{form.fields?.length || 0} campos · Creado {formatDate(form.created_at)}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Users size={12} className="text-[#A38560]" />
                    <span className="text-sm text-[#A38560] font-medium tabular-nums">{form.lead_count || 0} leads generados</span>
                  </div>
                  {form.default_broker_name && (
                    <div data-testid={`default-broker-${form.id}`} className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#A38560]/10 border border-[#A38560]/25">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#A38560]" />
                      <span className="text-[11px] text-[#A38560]">Auto-asignación: <b>{form.default_broker_name}</b></span>
                    </div>
                  )}
                  <div data-testid={`traffic-type-${form.id}`}
                    className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                      form.traffic_type === 'pauta' ? 'bg-[#6BCB77]/10 border-[#6BCB77]/25 text-[#6BCB77]' :
                      form.traffic_type === 'organico' ? 'bg-amber-500/10 border-amber-500/25 text-amber-300' :
                      'bg-white/5 border-white/10 text-white/50'}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    <span className="text-[11px]">
                      {form.traffic_type === 'pauta' ? 'Pauta · reporta a Meta' : form.traffic_type === 'organico' ? 'Organico · no reporta' : 'Automatico'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                <button onClick={() => startEdit(form)} data-testid={`edit-form-${form.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 text-sm border border-white/10 transition-colors">
                  <Pencil size={14} /> Editar
                </button>
                <button onClick={() => toggleActive(form)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 text-sm border border-white/10 transition-colors">
                  {form.active ? <EyeOff size={14} /> : <Eye size={14} />} {form.active ? 'Desactivar' : 'Activar'}
                </button>
                <button onClick={() => setShowEmbed(showEmbed === form.id ? null : form.id)} data-testid={`embed-btn-${form.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#A38560]/10 text-[#A38560] hover:bg-[#A38560]/20 text-sm border border-[#A38560]/20 transition-colors">
                  <Code size={14} /> Codigo
                </button>
                <button onClick={() => copyEmbed(form)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#6BCB77]/10 text-[#6BCB77] hover:bg-[#6BCB77]/20 text-sm border border-[#6BCB77]/20 transition-colors">
                  {copied === form.id ? <Check size={14} /> : <Copy size={14} />} Copiar
                </button>
                <button onClick={() => deleteForm(form.id)} className="px-3 py-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {/* Embed Preview + Code */}
            {showEmbed === form.id && (
              <div className="mt-4 bg-[#16302B] rounded-xl border border-white/5 p-4" data-testid={`embed-panel-${form.id}`}>
                <div className="flex items-center gap-1 mb-3 bg-[#0A1F1A] rounded-lg p-1 w-fit">
                  {[['preview', 'Vista previa', Eye], ['code', 'Código', Code]].map(([k, lbl, Icon]) => (
                    <button key={k} onClick={() => setEmbedTab(k)} data-testid={`embed-tab-${k}-${form.id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${embedTab === k ? 'bg-[#A38560] text-[#03110D] font-semibold' : 'text-white/50 hover:text-white'}`}>
                      <Icon size={12} /> {lbl}
                    </button>
                  ))}
                </div>
                {embedTab === 'preview' ? (
                  <div>
                    <p className="text-xs text-white/40 mb-2">Así se verá el formulario en tu landing. En esta vista el envío está desactivado.</p>
                    <iframe title={`preview-${form.id}`} data-testid={`embed-preview-iframe-${form.id}`} sandbox="allow-scripts allow-same-origin allow-modals"
                      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><script>window.fetch=function(){alert('Vista previa: el envío está desactivado.');return Promise.reject(new Error('preview'));};<\/script></head><body style="margin:0;padding:32px 24px;background:#fff">${generateEmbedCode(form, apiUrl, tyPages)}</body></html>`}
                      className="w-full bg-white rounded-lg border border-white/10" style={{ height: 900 }} />
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-white/40 mb-2">Copia y pega este codigo en tu landing page:</p>
                    <pre className="text-xs text-[#A38560] font-mono overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap break-all select-all">
                      {generateEmbedCode(form, apiUrl, tyPages)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {forms.length === 0 && (
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-12 text-center">
            <FileText size={48} strokeWidth={1} className="text-white/10 mx-auto mb-4" />
            <p className="text-white/40 text-sm">No tienes formularios creados</p>
            <p className="text-white/30 text-xs mt-1">Crea uno para empezar a capturar leads desde tus landing pages</p>
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div onClick={e => e.stopPropagation()} data-testid="create-form-modal"
            className="bg-[#0A1F1A] rounded-2xl border border-white/10 p-5 lg:p-6 w-full max-w-xl max-h-[90vh] overflow-auto space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-outfit text-lg font-medium text-white">{editingForm ? 'Editar Formulario' : 'Nuevo Formulario'}</h2>
              <button onClick={closeModal} className="text-white/30 hover:text-white"><X size={18} /></button>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Nombre del Formulario *</Label>
              <Input value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} placeholder="Ej: Landing Rosales" className={inputCls} data-testid="form-name-input" />
              <p className="text-[10px] text-white/20 mt-1">Los Thank You Pages son globales — se configuran arriba, en la caja "Thank You Pages globales", y aplican a todos los formularios.</p>
            </div>

            {/* Datos del inmueble (se muestran arriba del formulario en modo lectura) */}
            <div className="rounded-xl border border-white/10 bg-[#16302B]/40 p-3 space-y-3">
              <div>
                <Label className="text-[#A38560] text-[11px] uppercase tracking-wider font-semibold">Datos del inmueble</Label>
                <p className="text-[10px] text-white/40 mt-0.5">Se muestran como texto de lectura en la parte superior del formulario. El cliente no los edita.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-white/50 text-[10px] uppercase tracking-wider mb-1 block">Precio</Label>
                  <Input value={newForm.property_price || ''} onChange={e => setNewForm({ ...newForm, property_price: e.target.value })}
                    placeholder="Ej: 2.000.000.000" className={inputCls} data-testid="form-property-price" />
                </div>
                <div>
                  <Label className="text-white/50 text-[10px] uppercase tracking-wider mb-1 block">Ubicación</Label>
                  <Input value={newForm.property_location || ''} onChange={e => setNewForm({ ...newForm, property_location: e.target.value })}
                    placeholder="Ej: Rosales · Bogotá" className={inputCls} data-testid="form-property-location" />
                </div>
                <div>
                  <Label className="text-white/50 text-[10px] uppercase tracking-wider mb-1 block">Área</Label>
                  <Input value={newForm.property_size || ''} onChange={e => setNewForm({ ...newForm, property_size: e.target.value })}
                    placeholder="Ej: 54 m² — 200 m²" className={inputCls} data-testid="form-property-size" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Tipo de trafico (Meta)</Label>
              <Select value={newForm.traffic_type || 'auto'}
                onValueChange={v => setNewForm({ ...newForm, traffic_type: v })}>
                <SelectTrigger className={inputCls} data-testid="form-traffic-type"><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="pauta">Pauta (landing) — se reporta a Meta</SelectItem>
                  <SelectItem value="organico">Organico — nunca se reporta a Meta</SelectItem>
                  <SelectItem value="auto">Automatico — solo si llega con clic de anuncio</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-white/20 mt-1">En "Organico" no se guarda ni el fbclid, asi que jamas se le atribuye a una pauta. Al crear, se sugiere solo: nombres con "Landing" quedan en Pauta y los que empiezan con "Organico -" en Organico.</p>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Broker por defecto (auto-asignación)</Label>
              <Select value={newForm.default_broker_id || '_none'}
                onValueChange={v => setNewForm({ ...newForm, default_broker_id: v === '_none' ? '' : v })}>
                <SelectTrigger className={inputCls} data-testid="form-default-broker"><SelectValue placeholder="Sin auto-asignación (queda sin broker)" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="_none">Sin auto-asignación</SelectItem>
                  {brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-white/20 mt-1">Todo lead que llegue por este formulario se asignará automáticamente a este broker. Las reglas de "Enrutamiento por campaña" (Config CRM) tienen prioridad si el nombre de la campaña Meta coincide.</p>
            </div>

            {/* Preguntas fijas del formulario */}
            <div className="rounded-xl border border-white/10 bg-[#16302B]/40 p-3 space-y-2">
              <Label className="text-[#A38560] text-[11px] uppercase tracking-wider font-semibold">Preguntas del formulario</Label>
              <ul className="text-xs text-white/60 space-y-1" data-testid="fixed-questions-list">
                <li><b className="text-white/80">A. Nombre completo</b> · texto → Nombre (CRM)</li>
                <li><b className="text-white/80">B. Teléfono</b> · teléfono → Teléfono (CRM)</li>
                <li><b className="text-white/80">C. ¿Cuándo debes tomar la decisión?</b> · lista → Notas (CRM) <span className="text-white/35">(Inmediato · De 1 a 3 meses · De 3 a 6 meses · Más de 6 meses)</span></li>
                <li><b className="text-white/80">D. Presupuesto máximo</b> · lista → Presupuesto (CRM) + etiqueta contacto / cliente potencial</li>
              </ul>
            </div>

            {/* Opciones del Presupuesto */}
            <div className="rounded-xl border border-white/10 bg-[#16302B]/40 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#A38560] text-[11px] uppercase tracking-wider font-semibold">Opciones de "Presupuesto máximo"</Label>
                  <p className="text-[10px] text-white/40 mt-0.5">Reemplaza los "…" por valores (ej: "Menos de 1.300 millones"). La opción que empieza con <span className="text-[#A38560] font-semibold">"Menos de…"</span> etiqueta el lead como <b>contacto</b> (Thank You de contacto). "Entre… y…" y "Más de…" lo etiquetan como <b>cliente potencial</b> (Thank You de potencial). El mayor valor de la opción se guarda como Presupuesto en el CRM.</p>
                </div>
                <button onClick={() => setNewForm({ ...newForm, budget_options: [...(newForm.budget_options || []), ''] })}
                  data-testid="add-budget-option-btn"
                  className="flex items-center gap-1 text-xs text-[#A38560] hover:text-[#C4A87C] shrink-0">
                  <Plus size={12} /> Agregar
                </button>
              </div>
              <div className="space-y-2">
                {(newForm.budget_options || []).map((opt, idx) => {
                  const isLow = /^menos/i.test((opt || '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${isLow ? 'bg-gray-400' : 'bg-black border border-white/40'}`} title={isLow ? 'contacto' : 'cliente potencial'} />
                      <Input value={opt}
                        onChange={e => {
                          const next = [...(newForm.budget_options || [])];
                          next[idx] = e.target.value;
                          setNewForm({ ...newForm, budget_options: next });
                        }}
                        placeholder="Ej: Entre 1.300 y 1.800 millones"
                        className={inputCls}
                        data-testid={`budget-option-${idx}`}
                      />
                      <button onClick={() => {
                        const next = [...(newForm.budget_options || [])];
                        next.splice(idx, 1);
                        setNewForm({ ...newForm, budget_options: next });
                      }} className="text-white/30 hover:text-red-400 shrink-0 p-1"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-white/50 text-xs uppercase tracking-wider">Preguntas adicionales (opcional)</Label>
                <button onClick={addField} data-testid="add-form-field-btn"
                  className="flex items-center gap-1 text-xs text-[#A38560] hover:text-[#C4A87C] transition-colors">
                  <Plus size={12} /> Agregar campo
                </button>
              </div>
              <div className="space-y-3">
                {newForm.fields.map((field, idx) => (
                  <div key={idx} className="bg-[#16302B] rounded-xl border border-white/5 p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={field.label} onChange={e => updateField(idx, { label: e.target.value })} placeholder="Nombre del campo" className={inputCls} />
                      <Select value={field.type} onValueChange={v => updateField(idx, { type: v })}>
                        <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Mapear a campo CRM</label>
                      <Select value={field.crm_map || ''} onValueChange={v => updateField(idx, { crm_map: v === '_none' ? '' : v })}>
                        <SelectTrigger className={`${inputCls} h-9 text-xs`}><SelectValue placeholder="Sin mapear (extra)" /></SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          {CRM_FIELD_MAP.map(m => <SelectItem key={m.value || '_none'} value={m.value || '_none'}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {field.type === 'select' && (
                      <Input value={field.options || ''} onChange={e => updateField(idx, { options: e.target.value })} placeholder="Opciones separadas por coma" className={inputCls} />
                    )}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
                        <input type="checkbox" checked={field.required || false} onChange={e => updateField(idx, { required: e.target.checked })}
                          className="w-3.5 h-3.5 rounded border-[#A38560]" /> Obligatorio
                      </label>
                      <button onClick={() => removeField(idx)} className="text-white/20 hover:text-red-400 text-xs flex items-center gap-1">
                        <Trash2 size={12} /> Quitar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={createForm} data-testid="save-form-btn"
              className="w-full bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold py-2.5 rounded-xl transition-all active:scale-[0.98]">
              {editingForm ? 'Guardar Cambios' : 'Crear Formulario'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
